import React from 'react';
import DynamicTable from '../widgets/dynamicTable/DynamicTable.jsx';
import PropTypes from 'prop-types';
import HelperMetiers360 from '../../services/HelpersMetiers360.js';
import AuthorizationChecker from '../../services/AuthorizationChecker.js';
import { useToast } from '../../hooks/useToast.jsx';
import { useModal } from '../../hooks/useModal.jsx';
import { useStoreActions, useStoreState } from 'easy-peasy';
import { Alert, Button } from 'react-bootstrap';
import LogsComponent from "../logs/LogsComponent.jsx";
import RelationTypeFormComponent, { submitRelationTypeForm } from './RelationTypeFormComponent.jsx';

/**
 * 
 * @param {Array} relationTypesToDisplay array of all client relationTypes to be rendered
 
 * @example
 *  <RelationTypesTableComponent relationTypesToDisplay={relationTypes} />
 * 
 */

const RelationTypesTableComponent = ({ relationTypesToDisplay = [] }) => {
    const { allRelationTypes } = useStoreState(state => state.relationTypes);
    const { updateRelationType, deleteRelationType } = useStoreActions(actions => actions.relationTypes);
    const toast = useToast();
    const { modalComponent, setModalShow, setModalData, modalData, setIsSending } = useModal();

    const openEditModal = (relationType) => {
        const handleSubmit = () => {
            setIsSending(true);
            submitRelationTypeForm({ relationType, allRelationTypes, action: updateRelationType })
                .then(() => {
                    toast.success();
                    setModalShow(false);
                })
                .finally(() => setIsSending(false));
        }

        setModalData({
            ...modalData,
            header: <>Edition de la nature de la relation client</>,
            content: <RelationTypeFormComponent relationType={relationType} />,
            cancelButton: 'Annuler',
            onValidate: handleSubmit,
            size: 'xl',
        });
        setModalShow(true);
    }

    const openDeleteModal = (relationType) => {
        setModalData({
            ...modalData,
            header: <>Suppression de la nature de la relation client</>,
            content: <>
                <h5>
                    Êtes vous sûr⋅e de vouloir supprimer la nature de la relation client<strong> {relationType.name}</strong> &nbsp;?
                </h5>
                {relationType.countClients > 0 &&
                    <Alert variant='danger'>
                        <strong>Attention :</strong> La nature de la relation sera retirée de <strong>{relationType.countClients}</strong> clients !
                    </Alert>}
            </>,
            resetButton: 'Supprimer',
            cancelButton: 'Annuler',
            onReset: () => {
                deleteRelationType({ uniqueId: relationType.uniqueId })
                    .then(() => {
                        toast.success();
                        setModalShow(false);
                    })
                    .finally(() => setIsSending(false));
            },
        });
        setModalShow(true);
    }

    const sortType = [
        {
            value: 'name', label: 'Nom',
            test: HelperMetiers360.isArrayContainsValue(relationTypesToDisplay, "name"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.name, b.name),
            display: (relationType) => relationType.name
        },
        {
            value: 'countClients', label: 'Occurrence',
            test: HelperMetiers360.isArrayContainsValue(relationTypesToDisplay, "countClients"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.countClients, b.countClients),
            display: (relationType) => relationType.countClients
        },
        {
            value: 'edit', label: 'Éditer',
            test: AuthorizationChecker.hasUpdateRights('clientRelationTypes'),
            display: (relationType) => <Button variant="secondary" size="sm" onClick={() => { openEditModal(relationType) }}>
                <i className="fas fa-edit"></i>
            </Button>
        },
        {
            value: 'delete', label: 'Supprimer',
            test: AuthorizationChecker.hasDeleteRights('clientRelationTypes'),
            display: (relationType) => <Button variant="danger" size="sm" onClick={() => { openDeleteModal(relationType) }}>
                <i className="fas fa-trash-alt"></i>
            </Button>
        },
        {
            value: 'actionLogs', label: "Logs",
            test: AuthorizationChecker.isAdmin(),
            display: (relationType) => <LogsComponent entityNameFromComponent="relationTypes" uniqueIdFromComponent={relationType.uniqueId} />,
            flatDisplay: () => null
        }
    ];

    return <>
        <DynamicTable
            contentTable={relationTypesToDisplay}
            contentSort={sortType}
            valueInitSort="name"
            index='uniqueId'
        />
        {modalComponent}
    </>
}

RelationTypesTableComponent.propTypes = {
    relationTypesToDisplay: PropTypes.array.isRequired
};
export default RelationTypesTableComponent;